/**
 * SoftwareDesignRelationshipPack — 6 categories, ~20 subtypes.
 *
 * Single source of truth for edge styling across all 11 software-design
 * domains. Connectors, hydrators, and renderers all read from this pack
 * at registration time. See plans/software-design/foundation-scaffold.md
 * § Layer 2 — Relationship Pack for the rationale.
 *
 * This file is a scaffold — edge styling (colors, dashed patterns,
 * arrowheads) is declared as TODOs that Epic B fills per shape manifest
 * authoring. The structure is stable.
 */

import type {
  RelationshipCategoryManifest,
  RelationshipPack,
  RelationshipSubtypeManifest,
} from '../../../lib/diagram/relationship-manifest.types';

export type SoftwareDesignCategoryId =
  | 'inheritance'
  | 'composition'
  | 'dependency'
  | 'data'
  | 'behavioral'
  | 'architectural';

export type SoftwareDesignCategory = RelationshipCategoryManifest & {
  id: SoftwareDesignCategoryId;
};

export type SoftwareDesignSubtype = RelationshipSubtypeManifest & {
  categoryId: SoftwareDesignCategoryId;
  appliesTo: string[];
};

export const SoftwareDesignRelationshipPack: RelationshipPack = {
  meta: {
    id: 'software-design-relationships',
    name: 'Software Design Relationships',
    version: '1.0.0',
  },
  diagramType: 'software-design',

  categories: [
    { id: 'inheritance', label: 'Inheritance & Generalization', order: 1 },
    { id: 'composition', label: 'Composition & Aggregation', order: 2 },
    { id: 'dependency', label: 'Dependency & Import', order: 3 },
    { id: 'data', label: 'Data & Foreign Keys', order: 4 },
    { id: 'behavioral', label: 'Messages & Transitions', order: 5 },
    { id: 'architectural', label: 'API & Protocol', order: 6 },
  ],

  subtypes: [
    // ── Inheritance ────────────────────────────────────────────────
    {
      id: 'extends',
      categoryId: 'inheritance',
      label: 'Extends',
      appliesTo: ['class'],
      ontology: {
        predicate: 'extends',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'triangle' },
    },
    {
      id: 'implements',
      categoryId: 'inheritance',
      label: 'Implements',
      appliesTo: ['class'],
      ontology: {
        predicate: 'implements',
        status: 'profile_only',
        facets: ['responsibility', 'dependency'],
      },
      defaultStyle: { arrowhead: 'triangle', strokeDasharray: '4 2' },
    },
    {
      id: 'uc_generalization',
      categoryId: 'inheritance',
      label: 'Generalizes',
      appliesTo: ['usecase'],
      ontology: {
        predicate: 'generalizes',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'triangle' },
    },

    // ── Composition ────────────────────────────────────────────────
    {
      id: 'composes',
      categoryId: 'composition',
      label: 'Composition',
      appliesTo: ['class'],
      ontology: {
        predicate: 'composes',
        status: 'profile_only',
        facets: ['ownership', 'dependency'],
      },
      defaultStyle: { arrowhead: 'diamond' },
    },
    {
      id: 'aggregates',
      categoryId: 'composition',
      label: 'Aggregation',
      appliesTo: ['class'],
      ontology: {
        predicate: 'aggregates',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'diamond' },
    },
    {
      id: 'contains',
      categoryId: 'composition',
      label: 'Contains',
      appliesTo: ['class', 'component', 'c4', 'database'],
      ontology: {
        predicate: 'contains',
        status: 'profile_only',
      },
      defaultStyle: { arrowhead: 'none' },
    },

    // ── Dependency ─────────────────────────────────────────────────
    {
      id: 'imports',
      categoryId: 'dependency',
      label: 'Imports',
      appliesTo: ['class', 'component'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'imports' },
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '4 2' },
    },
    {
      id: 'uses',
      categoryId: 'dependency',
      label: 'Uses',
      appliesTo: ['class', 'component', 'c4'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'uses' },
      },
      defaultStyle: { arrowhead: 'open' },
    },
    {
      id: 'uc_include',
      categoryId: 'dependency',
      label: '<<include>>',
      appliesTo: ['usecase'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'include' },
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '4 2' },
    },
    {
      id: 'uc_extend',
      categoryId: 'dependency',
      label: '<<extend>>',
      appliesTo: ['usecase'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'extend' },
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '4 2' },
    },
    {
      id: 'association',
      categoryId: 'dependency',
      label: 'Association',
      appliesTo: ['class', 'usecase', 'component', 'c4', 'erd', 'database'],
      ontology: {
        predicate: 'association',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'open' },
    },

    // ── Data ───────────────────────────────────────────────────────
    {
      id: 'fk',
      categoryId: 'data',
      label: 'Foreign Key',
      appliesTo: ['erd', 'database'],
      ontology: {
        predicate: 'references',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { referenceType: 'foreign_key' },
      },
      defaultStyle: { arrowhead: 'circle' },
    },
    {
      id: 'db_ref',
      categoryId: 'data',
      label: 'Reference',
      appliesTo: ['database'],
      ontology: {
        predicate: 'references',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'circle' },
    },
    {
      id: 'dfd_dataflow',
      categoryId: 'data',
      label: 'Data Flow',
      appliesTo: ['dfd'],
      ontology: {
        predicate: 'data_flow',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'triangle' },
    },

    // ── Behavioral ─────────────────────────────────────────────────
    {
      id: 'sync_call',
      categoryId: 'behavioral',
      label: 'Sync Call',
      appliesTo: ['sequence'],
      ontology: {
        predicate: 'message_flow',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { messageType: 'sync_call' },
      },
      defaultStyle: { arrowhead: 'triangle' },
    },
    {
      id: 'async_call',
      categoryId: 'behavioral',
      label: 'Async Message',
      appliesTo: ['sequence'],
      ontology: {
        predicate: 'message_flow',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { messageType: 'async_call' },
      },
      defaultStyle: { arrowhead: 'open' },
    },
    {
      id: 'return_msg',
      categoryId: 'behavioral',
      label: 'Return',
      appliesTo: ['sequence'],
      ontology: {
        predicate: 'message_flow',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { messageType: 'return' },
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '4 2' },
    },
    {
      id: 'state_transition',
      categoryId: 'behavioral',
      label: 'Transition',
      appliesTo: ['state'],
      ontology: {
        predicate: 'sequence_flow',
        status: 'profile_only',
        facets: ['lifecycle', 'dependency'],
      },
      defaultStyle: { arrowhead: 'triangle' },
    },

    // ── Architectural ──────────────────────────────────────────────
    {
      id: 'c4_uses',
      categoryId: 'architectural',
      label: 'Uses',
      appliesTo: ['c4'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'c4_uses' },
      },
      defaultStyle: { arrowhead: 'open' },
    },
    {
      id: 'api_contract',
      categoryId: 'architectural',
      label: 'API Contract',
      appliesTo: ['component', 'c4'],
      ontology: {
        predicate: 'api_contract',
        status: 'profile_only',
        facets: ['governance', 'dependency'],
      },
      defaultStyle: { arrowhead: 'open' },
    },
    {
      id: 'adr_supersedes',
      categoryId: 'architectural',
      label: 'Supersedes',
      appliesTo: ['adr'],
      ontology: {
        predicate: 'supersedes',
        status: 'profile_only',
        facets: ['lifecycle'],
      },
      defaultStyle: { arrowhead: 'triangle', strokeDasharray: '8 2' },
    },
    {
      id: 'adr_led_to',
      categoryId: 'architectural',
      label: 'Led To',
      appliesTo: ['adr'],
      ontology: {
        predicate: 'led_to',
        status: 'profile_only',
        facets: ['lifecycle', 'dependency'],
      },
      defaultStyle: { arrowhead: 'open' },
    },
  ],
};
